import React from "react";

export const LegalLayout = ({ title, subtitle, updated, children }) => (
  <div className="max-w-3xl mx-auto px-6 py-16">
    <p className="label-tiny text-brand-gold">Legal</p>
    <h1 className="font-serif-brand text-4xl lg:text-5xl text-brand-charcoal mt-2">{title}</h1>
    {subtitle && <p className="text-brand-muted mt-2">{subtitle}</p>}
    {updated && <p className="text-xs text-brand-muted mt-3">Last updated: {updated}</p>}
    <div className="gold-divider my-8" />
    <div className="prose prose-sm max-w-none text-brand-charcoal/90 leading-relaxed space-y-6">
      {children}
    </div>
  </div>
);

const H2 = ({ children }) => <h2 className="font-serif-brand text-2xl text-brand-maroon mt-10 mb-3">{children}</h2>;
const P = ({ children }) => <p className="text-[15px] leading-relaxed">{children}</p>;
const UL = ({ children }) => <ul className="list-disc pl-6 space-y-1.5 text-[15px]">{children}</ul>;

// ============================================================
export const PrivacyPolicy = () => (
  <LegalLayout title="Privacy Policy" subtitle="Your data. Handled with the same care we give our jewellery." updated="10 Feb 2026">
    <P>Bindni Jewels ("we", "us", "our") respects your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard the information of visitors and customers of https://www.bindnijewels.com (the "Site").</P>

    <H2>1. Information We Collect</H2>
    <UL>
      <li><b>Account & Order data</b> — name, email, phone, shipping address, order history.</li>
      <li><b>Payment metadata</b> — payment method type and transaction ID. Card / UPI credentials are handled entirely by our PCI-DSS compliant gateway; we never store them.</li>
      <li><b>Google Sign-In profile</b> — name, email, profile picture (only if you sign in via Google).</li>
      <li><b>Device & usage</b> — IP, browser, pages viewed, referrer (via cookies/analytics).</li>
      <li><b>Support & marketing</b> — messages you send via contact form / chat / WhatsApp; newsletter email.</li>
    </UL>

    <H2>2. How We Use Your Information</H2>
    <UL>
      <li>Fulfil and deliver your orders, provide order updates and customer support.</li>
      <li>Personalise your experience, remember your cart & wishlist.</li>
      <li>Send order confirmations and, if you opt in, marketing emails and offers.</li>
      <li>Detect fraud and enforce our Terms of Service.</li>
      <li>Comply with legal obligations under Indian law.</li>
    </UL>

    <H2>3. Cookies & Analytics</H2>
    <P>We use essential cookies (session, cart) and may use anonymised analytics cookies to improve the Site. You can control cookies via your browser settings.</P>

    <H2>4. Sharing With Third Parties</H2>
    <UL>
      <li>Logistics partners for delivery.</li>
      <li>Payment gateway (Stripe / Razorpay) for processing.</li>
      <li>Email / SMS / WhatsApp service providers for notifications.</li>
      <li>Legal authorities when required by valid legal process.</li>
    </UL>
    <P>We do NOT sell your personal information to third parties.</P>

    <H2>5. Data Retention</H2>
    <P>We retain account and order data for as long as your account is active or as needed to provide services and comply with tax/legal obligations (typically 7 years for invoice records under Indian law).</P>

    <H2>6. Your Rights</H2>
    <UL>
      <li>Access, correct or delete your personal information.</li>
      <li>Opt out of marketing at any time (unsubscribe link in every email).</li>
      <li>Withdraw consent where processing is based on consent.</li>
    </UL>
    <P>Email <a className="text-brand-maroon" href="mailto:care@bindnijewels.com">care@bindnijewels.com</a> to exercise these rights. We respond within 30 days.</P>

    <H2>7. Data Security</H2>
    <P>All data is transmitted over HTTPS (TLS 1.2+). Passwords aren't stored (we use Google Sign-In). Sessions are httpOnly + Secure cookies with a 7-day expiry.</P>

    <H2>8. Children</H2>
    <P>The Site is intended for users aged 18 and above. We do not knowingly collect data from children under 18.</P>

    <H2>9. Changes to this Policy</H2>
    <P>We may update this Policy from time to time. The "Last updated" date reflects the most recent revision. Material changes will be highlighted on the Site.</P>

    <H2>10. Contact Us</H2>
    <P>Bindni Jewels · 10 B, Pushp Vihar Colony Extension, Indore, Madhya Pradesh 452010, India · <a className="text-brand-maroon" href="mailto:care@bindnijewels.com">care@bindnijewels.com</a> · +91 79877 80925.</P>
  </LegalLayout>
);

// ============================================================
export const Terms = () => (
  <LegalLayout title="Terms of Service" updated="10 Feb 2026">
    <P>These Terms of Service ("Terms") govern your use of the Bindni Jewels website and services. By accessing or purchasing from us, you agree to these Terms.</P>

    <H2>1. Eligibility</H2>
    <P>You must be at least 18 years old and legally capable of entering a binding contract under Indian law.</P>

    <H2>2. Products, Pricing & Availability</H2>
    <UL>
      <li>All prices are in Indian Rupees (INR) and inclusive of applicable GST.</li>
      <li>We reserve the right to correct pricing errors and to limit purchase quantities.</li>
      <li>Product photos are indicative; slight variations in colour or finish may occur due to the handcrafted nature of our jewellery.</li>
    </UL>

    <H2>3. Orders & Payment</H2>
    <P>Placing an order is an offer to purchase. Bindni Jewels reserves the right to accept or decline any order at its discretion. Payment is required at checkout for prepaid orders; COD is available on eligible orders. We use PCI-DSS compliant gateways — Bindni Jewels does not receive or store your card details.</P>

    <H2>4. Shipping & Delivery</H2>
    <P>See our <a className="text-brand-maroon" href="/shipping-policy">Shipping Policy</a>. Estimated timelines are indicative; delays due to logistics partners, courier hubs, weather, or public holidays are beyond our control.</P>

    <H2>5. Returns & Exchange</H2>
    <P>See our <a className="text-brand-maroon" href="/return-policy">15-Day Return & Exchange Policy</a>. Bridal ensembles, custom-made pieces, and items marked "final sale" are non-returnable.</P>

    <H2>6. Coupons & Discounts</H2>
    <P>Only one coupon code (BINDNI10 — 10% off on orders above ₹999) is active and cannot be combined with other offers. Coupons are void where prohibited and may be withdrawn without notice.</P>

    <H2>7. User Accounts</H2>
    <P>You are responsible for maintaining the confidentiality of your Google account used to sign in. You agree to notify us immediately of any unauthorised use.</P>

    <H2>8. Intellectual Property</H2>
    <P>All content, designs, product photography, logos, and copy on this Site are the exclusive property of Bindni Jewels and are protected under the Indian Copyright Act, 1957 and Trade Marks Act, 1999. No content may be reproduced without written permission.</P>

    <H2>9. Prohibited Uses</H2>
    <UL>
      <li>Reselling our products without written authorisation.</li>
      <li>Reverse-engineering, scraping, or bulk-downloading site content.</li>
      <li>Uploading offensive content in reviews or contact messages.</li>
      <li>Attempting to breach security, tamper with prices, or exploit vulnerabilities.</li>
    </UL>

    <H2>10. Limitation of Liability</H2>
    <P>Bindni Jewels' total liability arising out of or related to your use of the Site or purchase of a product is limited to the amount paid for the product in question. We are not liable for indirect, incidental, or consequential damages.</P>

    <H2>11. Governing Law & Jurisdiction</H2>
    <P>These Terms are governed by the laws of India. Any dispute shall be subject to the exclusive jurisdiction of the competent courts of Indore, Madhya Pradesh.</P>

    <H2>12. Changes</H2>
    <P>We may amend these Terms at any time. Continued use of the Site after changes constitutes acceptance of the revised Terms.</P>

    <H2>13. Contact</H2>
    <P>Bindni Jewels · 10 B, Pushp Vihar Colony Extension, Indore, Madhya Pradesh 452010, India · <a className="text-brand-maroon" href="mailto:care@bindnijewels.com">care@bindnijewels.com</a>.</P>
  </LegalLayout>
);

// ============================================================
export const ShippingPolicy = () => (
  <LegalLayout title="Shipping Policy" subtitle="Delivered PAN India with love and care." updated="10 Feb 2026">
    <H2>Delivery Timelines</H2>
    <UL>
      <li>Standard orders are processed within 24 hours (Mon-Sat).</li>
      <li>Delivery across India: <b>3-7 business days</b> depending on your city.</li>
      <li>Bridal & customised orders: <b>7-12 business days</b>.</li>
    </UL>

    <H2>Shipping Charges</H2>
    <UL>
      <li><b>Free shipping</b> on all orders above ₹999.</li>
      <li>Below ₹999 — flat ₹79 shipping fee.</li>
      <li>Cash on Delivery — additional ₹49 handling fee applies (COD available on orders below ₹5000).</li>
    </UL>

    <H2>Order Tracking</H2>
    <P>Once your order ships, you'll receive a tracking link via SMS + WhatsApp. You can also track anytime at <a className="text-brand-maroon" href="/track-order">/track-order</a>.</P>

    <H2>Delivery Partners</H2>
    <P>We ship via India Post, Delhivery, Bluedart, DTDC, and other trusted logistics partners depending on your PIN code.</P>

    <H2>Undelivered Packages</H2>
    <P>If a package is returned to us because it was undeliverable (incorrect address / repeated non-availability), we'll reach out to arrange re-shipment (nominal fee may apply) or a refund minus original shipping.</P>

    <H2>International Shipping</H2>
    <P>Currently, we ship only within India. For international enquiries, please DM us on WhatsApp or Instagram — we'd love to help.</P>
  </LegalLayout>
);

// ============================================================
export const ReturnPolicy = () => (
  <LegalLayout title="Return & Exchange Policy" subtitle="15-day, no-questions-asked returns." updated="10 Feb 2026">
    <H2>Return Window</H2>
    <P>You may request a return or exchange within <b>15 days</b> of delivery. Items must be unused, in original condition, with all original tags, packaging, and the invoice.</P>

    <H2>How to Initiate</H2>
    <UL>
      <li>Email <a className="text-brand-maroon" href="mailto:care@bindnijewels.com">care@bindnijewels.com</a> with your order number and a reason for return.</li>
      <li>OR message us on <a className="text-brand-maroon" href="https://wa.me/c/917987780925" target="_blank" rel="noreferrer">WhatsApp Catalogue</a>.</li>
      <li>We'll arrange a free reverse pickup from your address.</li>
    </UL>

    <H2>Refund Timeline</H2>
    <P>Once we receive and inspect the returned item, refunds are processed within <b>5-7 business days</b> to the original payment method. Bank credit timelines may add another 2-5 days depending on your issuing bank.</P>

    <H2>Exchange</H2>
    <P>You can exchange for another product of equal or higher value (pay the difference). New piece ships once the original is received and inspected.</P>

    <H2>Non-Returnable Items</H2>
    <UL>
      <li>Bridal ensembles and other customised orders.</li>
      <li>Earrings for hygiene reasons, if the seal/tag is broken.</li>
      <li>Items marked as "Final Sale" or "No Return" at checkout.</li>
      <li>Products damaged by misuse, wear, or improper care.</li>
    </UL>

    <H2>Damaged or Wrong Item Received</H2>
    <P>If your package arrives damaged or you received the wrong item, please email us within 48 hours of delivery with photos. We'll arrange an immediate replacement at no cost to you.</P>

    <H2>Cancellation</H2>
    <P>Orders can be cancelled free of cost within 12 hours of placement. Post 12 hours (or if already shipped) the return process applies.</P>
  </LegalLayout>
);
